import type {
  Approval,
  Asset,
  Attachment,
  BomPart,
  Phase,
  QualityCheck,
  ScanVersion,
  Stone,
  Trait,
} from "./types";

/* ============================================================
   Helpers
   ============================================================ */

function hash(a: number, b: number): number {
  const n = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function seedOf(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++)
    h = (h * 31 + (key.codePointAt(i) ?? 0)) % 100000;
  return h;
}

const DAY_MS = 86_400_000;

function parseDate(ddmmyyyy: string): Date {
  const [d, m, y] = ddmmyyyy.split("/").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

function shiftDays(ddmmyyyy: string, days: number): string {
  return formatDate(new Date(parseDate(ddmmyyyy).getTime() + days * DAY_MS));
}

/* ============================================================
   Seeds
   ============================================================ */

interface Seed {
  ref: string;
  title: string;
  categoryLabel: string;
  category: Asset["category"];
  material: Asset["material"];
  acquisition: Asset["acquisition"];
  axes: 2 | 3 | 0; // 0 = photogrammetry, no axis count
  traits: Trait[];
  trianglesM: number;
  textureK: 2048 | 4096 | 8192;
  sizeGb: number;
  date: string;
  location: string;
  progressPct: number;
  hoursSpent: number;
  hoursBudget: number;
  currentPhase: string;
  prototypist: string;
  versionCount: number;
  cadDeviation: number;
  /** Prefix for attachment file names */
  filePrefix: string;
  stones: Stone[];
}

const OPERATORS = [
  "M. Conti",
  "L. Bassi",
  "G. Neri",
  "S. Riva",
  "A. Ferri",
  "R. Galli",
];

const SEEDS: Seed[] = [
  {
    ref: "N-2022-0847",
    title: "Intreccio con Ametista Centrale",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "intreccio",
      "maglia-aperta",
      "microperle",
      "ametiste",
      "diamanti",
      "pendenti-goccia",
      "pietra-centrale",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 5.8,
    textureK: 8192,
    sizeGb: 3.12,
    date: "18/04/2022",
    location: "Caveau 3",
    progressPct: 132,
    hoursSpent: 211,
    hoursBudget: 160,
    currentPhase: "300 · Incassatura",
    prototypist: "M. Conti",
    versionCount: 4,
    cadDeviation: 0.14,
    filePrefix: "INT-0847",
    stones: [
      {
        position: "Ametista centrale",
        cut: "Ovale sfaccettato",
        caratsEach: 12.4,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 2214-8807",
      },
      {
        position: "Castoni ametista",
        cut: "Tondo sfaccettato",
        caratsEach: 0.42,
        quantity: 18,
        supplier: "Rosenthal",
        certificate: "GIA lotto 2214-B",
      },
      {
        position: "Diamanti di maglia",
        cut: "Vecchio taglio europeo",
        caratsEach: 0.18,
        quantity: 26,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Frangia pendente",
        cut: "Goccia",
        caratsEach: 0.22,
        quantity: 14,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 214,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "N-2023-1104",
    title: "Intreccio Perlato a Gocce",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "intreccio",
      "maglia-aperta",
      "microperle",
      "diamanti",
      "pendenti-goccia",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 5.2,
    textureK: 8192,
    sizeGb: 2.74,
    date: "02/09/2023",
    location: "Caveau 3",
    progressPct: 104,
    hoursSpent: 187,
    hoursBudget: 180,
    currentPhase: "350 · Lucidatura finale",
    prototypist: "S. Riva",
    versionCount: 3,
    cadDeviation: 0.07,
    filePrefix: "INT-1104",
    stones: [
      {
        position: "Frangia pendente",
        cut: "Goccia",
        caratsEach: 0.24,
        quantity: 16,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Diamanti di maglia",
        cut: "Vecchio taglio europeo",
        caratsEach: 0.16,
        quantity: 22,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 188,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "N-2021-0392",
    title: "Maglia Intrecciata con Ametiste",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "intreccio",
      "maglia-aperta",
      "microperle",
      "ametiste",
      "diamanti",
      "alta-densita",
      "multi-pezzo",
    ],
    trianglesM: 4.4,
    textureK: 4096,
    sizeGb: 2.05,
    date: "30/11/2021",
    location: "Caveau 1",
    progressPct: 98,
    hoursSpent: 143,
    hoursBudget: 146,
    currentPhase: "400 · Controllo finale",
    prototypist: "G. Neri",
    versionCount: 3,
    cadDeviation: 0.06,
    filePrefix: "MAG-0392",
    stones: [
      {
        position: "Castoni ametista",
        cut: "Tondo sfaccettato",
        caratsEach: 0.38,
        quantity: 22,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Diamanti di maglia",
        cut: "Vecchio taglio europeo",
        caratsEach: 0.15,
        quantity: 18,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 160,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "N-2024-0071",
    title: "Intreccio Graduato",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro giallo",
    acquisition: "Fotogrammetria",
    axes: 0,
    traits: [
      "intreccio",
      "maglia-aperta",
      "microperle",
      "ametiste",
      "diamanti",
      "pietra-centrale",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 4.9,
    textureK: 8192,
    sizeGb: 2.38,
    date: "14/02/2024",
    location: "Caveau 2",
    progressPct: 76,
    hoursSpent: 122,
    hoursBudget: 160,
    currentPhase: "300 · Incassatura",
    prototypist: "M. Conti",
    versionCount: 2,
    cadDeviation: 0.11,
    filePrefix: "GIR-0071",
    stones: [
      {
        position: "Ametista centrale",
        cut: "Ovale sfaccettato",
        caratsEach: 9.8,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 6188-2041",
      },
      {
        position: "Castoni ametista",
        cut: "Tondo sfaccettato",
        caratsEach: 0.34,
        quantity: 16,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 172,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "B-2022-0510",
    title: "Intreccio Perlato",
    categoryLabel: "Bracciale",
    category: "bracciale",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "intreccio",
      "maglia-aperta",
      "microperle",
      "ametiste",
      "diamanti",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 3.6,
    textureK: 8192,
    sizeGb: 1.82,
    date: "21/06/2022",
    location: "Caveau 3",
    progressPct: 112,
    hoursSpent: 168,
    hoursBudget: 150,
    currentPhase: "350 · Lucidatura finale",
    prototypist: "L. Bassi",
    versionCount: 4,
    cadDeviation: 0.09,
    filePrefix: "BRI-0510",
    stones: [
      {
        position: "Castoni ametista",
        cut: "Tondo sfaccettato",
        caratsEach: 0.3,
        quantity: 9,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Diamanti di maglia",
        cut: "Vecchio taglio europeo",
        caratsEach: 0.14,
        quantity: 12,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 96,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "E-2022-0301",
    title: "Intreccio a Goccia",
    categoryLabel: "Orecchini",
    category: "orecchini",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 2,
    traits: [
      "intreccio",
      "microperle",
      "ametiste",
      "diamanti",
      "pendenti-goccia",
      "multi-pezzo",
    ],
    trianglesM: 1.6,
    textureK: 4096,
    sizeGb: 0.68,
    date: "17/11/2022",
    location: "Caveau 2",
    progressPct: 100,
    hoursSpent: 72,
    hoursBudget: 72,
    currentPhase: "400 · Controllo finale",
    prototypist: "S. Riva",
    versionCount: 2,
    cadDeviation: 0.06,
    filePrefix: "ORI-0301",
    stones: [
      {
        position: "Castoni ametista",
        cut: "Tondo sfaccettato",
        caratsEach: 0.28,
        quantity: 2,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Pendente",
        cut: "Goccia",
        caratsEach: 0.2,
        quantity: 2,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Profilatura maglie",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 48,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },

  {
    ref: "N-2023-0288",
    title: "Fermezza, Cabochon di Ametista",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "cabochon",
      "ventaglio",
      "microperle",
      "ametiste",
      "diamanti",
      "giada",
      "pendente",
      "parure",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 7.4,
    textureK: 8192,
    sizeGb: 3.68,
    date: "07/03/2023",
    location: "Caveau 1",
    progressPct: 92,
    hoursSpent: 318,
    hoursBudget: 346,
    currentPhase: "350 · Lucidatura finale",
    prototypist: "G. Neri",
    versionCount: 3,
    cadDeviation: 0.08,
    filePrefix: "FER-0288",
    stones: [
      {
        position: "Pendente centrale",
        cut: "Cabochon ovale",
        caratsEach: 38.6,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 7741-0021",
      },
      {
        position: "Cabochon lavanda",
        cut: "Cabochon ovale",
        caratsEach: 24.2,
        quantity: 4,
        supplier: "Rosenthal",
        certificate: "GIA lotto 7741",
      },
      {
        position: "Cabochon ametista scura",
        cut: "Cabochon tondo",
        caratsEach: 6.4,
        quantity: 11,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Rosette verdi",
        cut: "Cabochon tondo",
        caratsEach: 1.1,
        quantity: 8,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Diamanti di ventaglio",
        cut: "Brillante rotondo",
        caratsEach: 0.16,
        quantity: 14,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Campitura ventagli",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 486,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "N-2021-0777",
    title: "Fermezza, Pendente Maggiore",
    categoryLabel: "Collana",
    category: "collana",
    material: "Oro rosa",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "cabochon",
      "ventaglio",
      "microperle",
      "ametiste",
      "diamanti",
      "pendente",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 6.6,
    textureK: 8192,
    sizeGb: 3.21,
    date: "25/09/2021",
    location: "Caveau 1",
    progressPct: 104,
    hoursSpent: 158,
    hoursBudget: 152,
    currentPhase: "400 · Controllo finale",
    prototypist: "M. Conti",
    versionCount: 3,
    cadDeviation: 0.07,
    filePrefix: "FER-0777",
    stones: [
      {
        position: "Pendente centrale",
        cut: "Cabochon ovale",
        caratsEach: 46.1,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 5520-1188",
      },
      {
        position: "Cabochon ametista scura",
        cut: "Cabochon tondo",
        caratsEach: 5.8,
        quantity: 9,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Diamanti di ventaglio",
        cut: "Brillante rotondo",
        caratsEach: 0.14,
        quantity: 12,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Campitura ventagli",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 402,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "E-2025-0140",
    title: "Fermezza, Pendenti in Set",
    categoryLabel: "Orecchini",
    category: "orecchini",
    material: "Oro giallo",
    acquisition: "Fotogrammetria",
    axes: 0,
    traits: [
      "cabochon",
      "ventaglio",
      "microperle",
      "ametiste",
      "giada",
      "pendente",
      "parure",
      "multi-pezzo",
    ],
    trianglesM: 2.1,
    textureK: 4096,
    sizeGb: 0.96,
    date: "09/01/2025",
    location: "Caveau 2",
    progressPct: 41,
    hoursSpent: 58,
    hoursBudget: 140,
    currentPhase: "250 · Rifinitura",
    prototypist: "S. Riva",
    versionCount: 2,
    cadDeviation: 0.16,
    filePrefix: "FER-0140",
    stones: [
      {
        position: "Pendente",
        cut: "Cabochon ovale",
        caratsEach: 11.4,
        quantity: 2,
        supplier: "Rosenthal",
        certificate: "GIA lotto 9014",
      },
      {
        position: "Cabochon ametista scura",
        cut: "Cabochon tondo",
        caratsEach: 3.2,
        quantity: 2,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Rosette verdi",
        cut: "Cabochon tondo",
        caratsEach: 0.9,
        quantity: 2,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Campitura ventagli",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 96,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "B-2024-0132",
    title: "Fermezza, Maglia a Ventaglio",
    categoryLabel: "Bracciale",
    category: "bracciale",
    material: "Oro giallo",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "cabochon",
      "ventaglio",
      "microperle",
      "ametiste",
      "diamanti",
      "giada",
      "parure",
      "alta-densita",
      "texture-8k",
      "multi-pezzo",
    ],
    trianglesM: 5.0,
    textureK: 8192,
    sizeGb: 2.44,
    date: "06/03/2024",
    location: "Caveau 3",
    progressPct: 62,
    hoursSpent: 149,
    hoursBudget: 240,
    currentPhase: "250 · Rifinitura",
    prototypist: "L. Bassi",
    versionCount: 2,
    cadDeviation: 0.1,
    filePrefix: "FER-0132",
    stones: [
      {
        position: "Cabochon ametista scura",
        cut: "Cabochon tondo",
        caratsEach: 5.2,
        quantity: 7,
        supplier: "Rosenthal",
        certificate: "GIA lotto 8890",
      },
      {
        position: "Rosette verdi",
        cut: "Cabochon tondo",
        caratsEach: 0.95,
        quantity: 6,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Diamanti di ventaglio",
        cut: "Brillante rotondo",
        caratsEach: 0.12,
        quantity: 9,
        supplier: "Grimaldi",
        certificate: "—",
      },
      {
        position: "Campitura ventagli",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 264,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },
  {
    ref: "P-2023-0489",
    title: "Fermezza, Ventaglio Cabochon",
    categoryLabel: "Spilla",
    category: "spilla",
    material: "Oro giallo",
    acquisition: "Fotogrammetria",
    axes: 0,
    traits: [
      "cabochon",
      "ventaglio",
      "microperle",
      "ametiste",
      "diamanti",
      "texture-8k",
    ],
    trianglesM: 2.6,
    textureK: 8192,
    sizeGb: 1.24,
    date: "30/05/2023",
    location: "Caveau 4",
    progressPct: 87,
    hoursSpent: 103,
    hoursBudget: 118,
    currentPhase: "300 · Incassatura",
    prototypist: "A. Ferri",
    versionCount: 3,
    cadDeviation: 0.08,
    filePrefix: "FER-0489",
    stones: [
      {
        position: "Cabochon lavanda",
        cut: "Cabochon ovale",
        caratsEach: 14.8,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 6603-4417",
      },
      {
        position: "Cabochon ametista scura",
        cut: "Cabochon tondo",
        caratsEach: 4.1,
        quantity: 4,
        supplier: "Rosenthal",
        certificate: "—",
      },
      {
        position: "Campitura ventagli",
        cut: "Perla tonda",
        caratsEach: 0.02,
        quantity: 138,
        supplier: "Casa Bellini",
        certificate: "—",
      },
    ],
  },

  {
    ref: "A-2023-0918",
    title: "Solitario Principessa su Pavé",
    categoryLabel: "Anello",
    category: "anello",
    material: "Platino",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "solitario",
      "griffe",
      "taglio-quadrato",
      "pave",
      "diamanti",
      "incassatura",
      "alta-densita",
      "texture-8k",
    ],
    trianglesM: 2.6,
    textureK: 8192,
    sizeGb: 1.29,
    date: "19/07/2023",
    location: "Caveau 3",
    progressPct: 88,
    hoursSpent: 141,
    hoursBudget: 160,
    currentPhase: "300 · Incassatura",
    prototypist: "S. Riva",
    versionCount: 3,
    cadDeviation: 0.09,
    filePrefix: "SOL-0918",
    stones: [
      {
        position: "Pietra centrale",
        cut: "Taglio principessa",
        caratsEach: 2.05,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 1904-3312",
      },
      {
        position: "Pavé gambo",
        cut: "Brillante rotondo",
        caratsEach: 0.03,
        quantity: 22,
        supplier: "Grimaldi",
        certificate: "—",
      },
    ],
  },
  {
    ref: "A-2025-0044",
    title: "Solitario Quadrato, Studio Gambo",
    categoryLabel: "Anello",
    category: "anello",
    material: "Oro bianco",
    acquisition: "Luce strutturata",
    axes: 3,
    traits: [
      "solitario",
      "griffe",
      "taglio-quadrato",
      "pave",
      "diamanti",
      "incassatura",
      "texture-8k",
    ],
    trianglesM: 2.2,
    textureK: 8192,
    sizeGb: 1.08,
    date: "22/01/2025",
    location: "Caveau 2",
    progressPct: 34,
    hoursSpent: 47,
    hoursBudget: 138,
    currentPhase: "200 · Microfusione",
    prototypist: "G. Neri",
    versionCount: 2,
    cadDeviation: 0.12,
    filePrefix: "SOL-0044",
    stones: [
      {
        position: "Pietra centrale",
        cut: "Taglio principessa",
        caratsEach: 1.62,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 4471-9902",
      },
      {
        position: "Pavé gambo",
        cut: "Brillante rotondo",
        caratsEach: 0.028,
        quantity: 20,
        supplier: "Grimaldi",
        certificate: "—",
      },
    ],
  },
  {
    ref: "A-2020-0233",
    title: "Solitario Griffe Alte",
    categoryLabel: "Anello",
    category: "anello",
    material: "Platino",
    acquisition: "Luce strutturata",
    axes: 2,
    traits: [
      "solitario",
      "griffe",
      "taglio-quadrato",
      "pave",
      "diamanti",
      "incassatura",
    ],
    trianglesM: 1.7,
    textureK: 4096,
    sizeGb: 0.71,
    date: "08/10/2020",
    location: "Caveau 1",
    progressPct: 93,
    hoursSpent: 84,
    hoursBudget: 90,
    currentPhase: "400 · Controllo finale",
    prototypist: "A. Ferri",
    versionCount: 3,
    cadDeviation: 0.04,
    filePrefix: "SOL-0233",
    stones: [
      {
        position: "Pietra centrale",
        cut: "Taglio principessa",
        caratsEach: 2.41,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 1102-7745",
      },
      {
        position: "Pavé gambo",
        cut: "Brillante rotondo",
        caratsEach: 0.025,
        quantity: 18,
        supplier: "Grimaldi",
        certificate: "—",
      },
    ],
  },
  {
    ref: "A-2019-0104",
    title: "Solitario Pavé a Gambo Affusolato",
    categoryLabel: "Anello",
    category: "anello",
    material: "Oro bianco",
    acquisition: "Fotogrammetria",
    axes: 0,
    traits: [
      "solitario",
      "griffe",
      "taglio-quadrato",
      "pave",
      "diamanti",
      "incassatura",
    ],
    trianglesM: 1.4,
    textureK: 2048,
    sizeGb: 0.52,
    date: "03/06/2019",
    location: "Caveau 4",
    progressPct: 81,
    hoursSpent: 26,
    hoursBudget: 32,
    currentPhase: "350 · Lucidatura finale",
    prototypist: "L. Bassi",
    versionCount: 2,
    cadDeviation: 0.03,
    filePrefix: "SOL-0104",
    stones: [
      {
        position: "Pietra centrale",
        cut: "Taglio principessa",
        caratsEach: 1.18,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 0904-2218",
      },
      {
        position: "Pavé gambo",
        cut: "Brillante rotondo",
        caratsEach: 0.022,
        quantity: 16,
        supplier: "Grimaldi",
        certificate: "—",
      },
    ],
  },
  {
    ref: "A-2021-0655",
    title: "Solitario Pavé Doppio Filetto",
    categoryLabel: "Anello",
    category: "anello",
    material: "Platino",
    acquisition: "Luce strutturata",
    axes: 2,
    traits: [
      "solitario",
      "griffe",
      "taglio-quadrato",
      "pave",
      "diamanti",
      "incassatura",
      "alta-densita",
    ],
    trianglesM: 2.0,
    textureK: 4096,
    sizeGb: 0.94,
    date: "12/05/2021",
    location: "Caveau 4",
    progressPct: 100,
    hoursSpent: 96,
    hoursBudget: 96,
    currentPhase: "400 · Controllo finale",
    prototypist: "A. Ferri",
    versionCount: 2,
    cadDeviation: 0.05,
    filePrefix: "SOL-0655",
    stones: [
      {
        position: "Pietra centrale",
        cut: "Taglio principessa",
        caratsEach: 1.94,
        quantity: 1,
        supplier: "Rosenthal",
        certificate: "GIA 3318-6640",
      },
      {
        position: "Pavé gambo",
        cut: "Brillante rotondo",
        caratsEach: 0.024,
        quantity: 24,
        supplier: "Grimaldi",
        certificate: "—",
      },
    ],
  },
];

/* ============================================================
   Derivation
   ============================================================ */

const PHASE_TEMPLATE: Array<Pick<Phase, "code" | "label">> = [
  { code: "100", label: "Progettazione CAD" },
  { code: "150", label: "Stampa prototipo in resina" },
  { code: "200", label: "Microfusione" },
  { code: "250", label: "Rifinitura e saldature" },
  { code: "300", label: "Incassatura pietre" },
  { code: "350", label: "Lucidatura finale" },
  { code: "400", label: "Controllo finale e CQ" },
];

function conformingCount(dev: number): number {
  if (dev <= 0.1) return 8;
  if (dev <= 0.2) return 7;
  if (dev <= 0.3) return 6;
  return 5;
}

function scanMethod(i: number, axes: number): string {
  if (i === 0 && axes === 0) return "Fotogrammetria";
  return `Luce strutturata · ${axes || 3} assi`;
}

function buildVersions(s: Seed): ScanVersion[] {
  const sd = seedOf(s.ref);
  const notes = [
    "Rilievo iniziale dal prototipo in resina",
    "Chiusura mesh, prima texture PBR",
    "Correzione retro e giunzioni",
    "Reincasso pietre, nuova texture",
  ];
  const out: ScanVersion[] = [];
  const n = s.versionCount;
  for (let i = 0; i < n; i++) {
    const isCurrent = i === n - 1;
    const back = Math.round((n - 1 - i) * (10 + 10 * hash(sd, 3.7 + i)));
    const dev = isCurrent
      ? s.cadDeviation
      : Math.round(
          (s.cadDeviation + (n - 1 - i) * (0.06 + 0.05 * hash(sd, 5.1))) * 100,
        ) / 100;
    const tri =
      Math.round(s.trianglesM * (0.38 + (0.62 * (i + 1)) / n) * 10) / 10;
    out.push({
      id: `v${i + 1}`,
      date: shiftDays(s.date, -back),
      author: OPERATORS[Math.floor(hash(sd, 9.3 + i) * OPERATORS.length)],
      method: scanMethod(i, s.axes),
      triangles: `${tri.toFixed(1)} M`,
      size: `${((s.sizeGb * tri) / s.trianglesM).toFixed(2)} GB`,
      changes: notes[(i + notes.length - n) % notes.length],
      status: isCurrent ? "corrente" : "archiviata",
      cadDeviation: dev,
      conforming: conformingCount(dev),
    });
  }
  return out;
}

function buildAttachments(s: Seed, versions: ScanVersion[]): Attachment[] {
  const sd = seedOf(s.ref);
  const cur = versions.at(-1)!;
  const first = versions[0];
  const mid = versions[Math.max(0, versions.length - 2)];
  const plan: Array<[Attachment["kind"], string, string, ScanVersion, number]> =
    [
      ["Render", "r01", "PNG 4096²", cur, 18.4],
      ["Render", "r02", "PNG 4096²", cur, 17.9],
      ["Render", "r03", "PNG 4096²", mid, 16.2],
      ["CAD", "master", "STEP", cur, 44.1],
      ["CAD", "retro", "STEP", mid, 12.6],
      ["Disegno tecnico", "tav01", "PDF", cur, 2.1],
      ["Disegno tecnico", "tav02", "PDF", cur, 1.8],
      ["Foto riferimento", "f01", "JPG", first, 6.4],
      ["Foto riferimento", "f02", "JPG", first, 6.1],
      ["Foto riferimento", "f03", "JPG", mid, 5.8],
      ["Foto riferimento", "f04", "JPG", mid, 5.9],
      ["Foto riferimento", "f05", "JPG", cur, 7.2],
      ["Foto riferimento", "f06", "JPG", cur, 7.0],
    ];
  return plan.map(([kind, suffix, format, ver, baseSize], i) => ({
    kind,
    name: `${s.filePrefix}-${suffix}`,
    format,
    version: ver.id,
    size: `${(baseSize * (0.82 + 0.36 * hash(sd, 11 + i))).toFixed(1)} MB`,
    date: ver.date,
  }));
}

function buildPhases(s: Seed): Phase[] {
  const sd = seedOf(s.ref);
  const currentCode = s.currentPhase.slice(0, 3);
  const currentIndex = PHASE_TEMPLATE.findIndex((p) => p.code === currentCode);
  // Work backwards from the acquisition date so the timeline lands on it.
  let cursor = -Math.round(38 + 14 * hash(sd, 2.2));
  return PHASE_TEMPLATE.map((tpl, i) => {
    const done = i < currentIndex;
    const running = i === currentIndex;
    const span = Math.round(4 + 8 * hash(sd, 4.4 + i));
    const start = shiftDays(s.date, cursor);
    cursor += span + 1;
    const end = done ? shiftDays(s.date, cursor - 1) : "—";
    const share = [0.16, 0.07, 0.12, 0.25, 0.4, 0.0, 0.0][i];
    const hours =
      done || running ? Math.max(4, Math.round(s.hoursSpent * share)) : null;
    let progress: number;
    let status: "completata" | "in corso" | "pianificata";
    if (done) {
      progress = 100;
      status = "completata";
    } else if (running) {
      progress = Math.round(40 + 45 * hash(sd, 8.8));
      status = "in corso";
    } else {
      progress = 0;
      status = "pianificata";
    }
    return {
      ...tpl,
      owner: OPERATORS[Math.floor(hash(sd, 6.6 + i) * OPERATORS.length)],
      start: done || running ? start : "—",
      end,
      hours,
      progress,
      status,
    };
  });
}

function buildQualityChecks(s: Seed): QualityCheck[] {
  const sd = seedOf(s.ref);
  const dev = s.cadDeviation;
  const wall = Math.round((0.86 + 0.2 * hash(sd, 12.1)) * 100) / 100;
  const seat = Math.round((0.2 + 0.28 * hash(sd, 13.2)) * 100) / 100;
  const flat = Math.round((0.02 + 0.03 * hash(sd, 14.3)) * 100) / 100;
  const weight = Math.round((0.7 + 1.1 * hash(sd, 15.4)) * 10) / 10;
  const meshMin = s.trianglesM >= 2 ? 2.0 : 0.5;
  const density = s.material === "Platino" ? "platino 21.45" : "oro 18kt 15.58";
  return [
    {
      label: "Tenuta mesh (watertight)",
      method: "Analisi topologica",
      threshold: "0 fori",
      measured: "0 fori",
      delta: "0",
      pass: true,
    },
    {
      label: "Densità mesh",
      method: "Conteggio triangoli",
      threshold: `≥ ${meshMin.toFixed(1)} M`,
      measured: `${s.trianglesM.toFixed(1)} M`,
      delta: `+${(s.trianglesM - meshMin).toFixed(1)} M`,
      pass: true,
    },
    {
      label: "Risoluzione texture",
      method: "Ispezione UV",
      threshold: `≥ ${s.textureK}²`,
      measured: `${s.textureK}²`,
      delta: "0",
      pass: true,
    },
    {
      label: "Deviazione CAD",
      method: "Confronto nuvola / CAD",
      threshold: "≤ 0.10 mm",
      measured: `${dev.toFixed(2)} mm`,
      delta: `${dev > 0.1 ? "+" : "−"}${Math.abs(dev - 0.1).toFixed(2)} mm`,
      pass: dev <= 0.1,
    },
    {
      label: "Spessore minimo parete",
      method: "Sezione automatica",
      threshold: "≥ 0.80 mm",
      measured: `${wall.toFixed(2)} mm`,
      delta: `+${(wall - 0.8).toFixed(2)} mm`,
      pass: wall >= 0.8,
    },
    {
      label: "Allineamento incastonature",
      method: "Scarto angolare",
      threshold: "≤ 0.50°",
      measured: `${seat.toFixed(2)}°`,
      delta: `-${(0.5 - seat).toFixed(2)}°`,
      pass: seat <= 0.5,
    },
    {
      label: "Planarità piano di posa",
      method: "Piano di best-fit",
      threshold: "≤ 0.05 mm",
      measured: `${flat.toFixed(2)} mm`,
      delta: `-${(0.05 - flat).toFixed(2)} mm`,
      pass: flat <= 0.05,
    },
    {
      label: "Peso teorico / reale",
      method: `Densità ${density}`,
      threshold: "≤ 2.0%",
      measured: `${weight.toFixed(1)}%`,
      delta: `-${(2 - weight).toFixed(1)}%`,
      pass: weight <= 2,
    },
  ];
}

function buildBom(s: Seed): BomPart[] {
  const mat = `${s.material} 18kt`;
  type K = BomPart["kind"];
  const p = (
    code: string,
    label: string,
    kind: K,
    qty: number,
    note?: string,
  ): BomPart => ({
    code,
    label,
    kind,
    material: mat,
    qty,
    ...(note ? { note } : {}),
  });
  const structural: Record<Asset["category"], BomPart[]> = {
    collana: [
      p("S-01", "Corpo collana", "struttura", 1),
      p(
        "S-02",
        "Maglie di collegamento",
        "elemento",
        s.traits.includes("intreccio") ? 48 : 24,
      ),
      p("S-03", "Anelli di giunzione", "elemento", 4),
      p("S-04", "Chiusura a moschettone", "chiusura", 1),
    ],
    bracciale: [
      p("S-01", "Corpo bracciale", "struttura", 1),
      p("S-02", "Maglie di collegamento", "elemento", 18),
      p("S-03", "Anelli di giunzione", "elemento", 2),
      p("S-04", "Chiusura a scatto", "chiusura", 1),
    ],
    anello: [
      p("S-01", "Gambo", "struttura", 1),
      p("S-02", "Castone", "elemento", 1),
      ...(s.traits.includes("griffe")
        ? [p("S-03", "Griffe", "elemento", 4, "4 griffe")]
        : []),
    ],
    orecchini: [
      p("S-01", "Corpo orecchino", "struttura", 2),
      p("S-02", "Perno", "chiusura", 2),
      p("S-03", "Farfallina di sicurezza", "chiusura", 2),
    ],
    spilla: [
      p("S-01", "Corpo spilla", "struttura", 1),
      p("S-02", "Ago", "chiusura", 1),
      p("S-03", "Chiusura ago", "chiusura", 1),
    ],
  };
  const stoneParts: BomPart[] = s.stones.map((st, i) => ({
    code: `P-${String(i + 1).padStart(2, "0")}`,
    label: st.position,
    kind: "pietra" as const,
    material: st.cut,
    qty: st.quantity,
    note: `${st.caratsEach.toFixed(2)} ct/pz`,
  }));
  return [...(structural[s.category] ?? []), ...stoneParts];
}

function buildApprovals(s: Seed, current: ScanVersion): Approval[] {
  const inDeroga = s.cadDeviation > 0.1;
  return [
    {
      role: "Responsabile prototipia",
      owner: s.prototypist,
      version: current.id,
      date: current.date,
      outcome: "approvata",
    },
    {
      role: "Responsabile CQ",
      owner: "R. Galli",
      version: current.id,
      date: shiftDays(current.date, 1),
      outcome: inDeroga ? "in deroga" : "approvata",
    },
    {
      role: "Direzione creativa",
      owner: "A. Ferri",
      version: current.id,
      date: "—",
      outcome: "in attesa",
    },
  ];
}

function buildAsset(s: Seed): Asset {
  const versions = buildVersions(s);
  const current = versions.at(-1)!;
  const checks = buildQualityChecks(s);
  const failing = checks.filter((c) => !c.pass);
  const acquisitionDetail =
    s.acquisition === "Fotogrammetria"
      ? "Fotogrammetria"
      : `Luce strutturata · ${s.axes} assi`;

  return {
    ref: s.ref,
    title: s.title,
    label: `${s.categoryLabel} — ${s.title}`,
    category: s.category,
    categoryLabel: s.categoryLabel,
    material: s.material,
    acquisition: s.acquisition,
    acquisitionDetail,
    traits: s.traits,

    triangles: `${s.trianglesM.toFixed(1)} M`,
    trianglesM: s.trianglesM,
    texture: `${s.textureK} x ${s.textureK} · PBR`,
    formats: "USDZ · OBJ",
    size: `${s.sizeGb.toFixed(2)} GB`,
    sizeGb: s.sizeGb,
    date: s.date,
    location: s.location,

    progressPct: s.progressPct,
    hoursSpent: s.hoursSpent,
    hoursBudget: s.hoursBudget,
    currentPhase: s.currentPhase,
    deliveryDate: shiftDays(s.date, 18),
    prototypist: s.prototypist,

    versions,
    attachments: buildAttachments(s, versions),
    phases: buildPhases(s),
    qualityChecks: checks,
    stones: s.stones,
    bom: buildBom(s),
    approvals: buildApprovals(s, current),

    qcOperator: "R. Galli",
    qcDate: current.date,
    qcStandard: "CQ-3D / 04",
    qcRelease: failing.length ? "In deroga" : "Rilasciata",
    qcAlert: failing.length
      ? `Deviazione CAD ${s.cadDeviation.toFixed(2)} mm, oltre la soglia di 0.10 mm.`
      : null,
  };
}

/* ============================================================
   Public surface
   ============================================================ */

export const ASSETS: Asset[] = SEEDS.map(buildAsset);

const BY_REF = new Map(ASSETS.map((a) => [a.ref, a]));

export const ARCHIVE_TOTAL = 12_847;

export function getAsset(ref: string | undefined): Asset | undefined {
  return ref ? BY_REF.get(ref) : undefined;
}

export const REFERENCE_REF = "N-2022-0847";
